import { useState } from 'react'
import { Alert, AuthLayout, Button, Link, PasswordField, TextField } from 'armxlib'
import { PageMeta } from '../../app/PageMeta'
import { en } from '../../i18n/en'
import { useCaptcha } from '../../lib/turnstile'
import { ROUTES } from '../../routes'
import { SiteFooter } from '../legal/SiteFooter'
import { confirmSignUp, failureReason, resendSignUpCode, signIn } from './api'
import { CodeStep } from './CodeStep'

const t = en.auth

/** `/login`: email, password and Turnstile. An unconfirmed account can enter its code from here. */
export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unconfirmed, setUnconfirmed] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const captcha = useCaptcha()

  async function submit() {
    if (!captcha.token) {
      setError(captcha.failed ? t.captchaFailed : t.captchaPending)
      return
    }
    setPending(true)
    setError(null)
    try {
      await signIn(email, password, captcha.token)
    } catch (failure) {
      const reason = failureReason(failure)
      setUnconfirmed(reason === 'email_not_confirmed')
      setError(t.errors[reason])
    } finally {
      captcha.reset()
      setPending(false)
    }
  }

  const meta = <PageMeta title={t.login.title} description={t.login.metaDescription} canonicalPath={ROUTES.login.path} />

  if (confirming) {
    return (
      <AuthLayout brand={en.site.name} title={t.code.title} description={t.code.description(email)} footer={<SiteFooter />}>
        {meta}
        <CodeStep
          submitLabel={t.login.submit}
          onSubmit={(code) => confirmSignUp(email, code)}
          onResend={(captchaToken) => resendSignUpCode(email, captchaToken)}
        />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      brand={en.site.name}
      title={t.login.title}
      description={t.login.description}
      footer={
        <>
          <span>
            {t.login.noAccount} <Link href={ROUTES.register.path}>{t.login.createOne}</Link>
          </span>
          <SiteFooter />
        </>
      }
    >
      {meta}
      <form
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        {error && (
          <Alert tone="danger">
            {error}
            {unconfirmed && (
              <>
                {' '}
                <button type="button" className="text-button" onClick={() => setConfirming(true)}>
                  {t.login.enterCode}
                </button>
              </>
            )}
          </Alert>
        )}
        <TextField
          label={t.email}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <PasswordField
          label={t.password}
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint={<Link href={ROUTES.resetPassword.path}>{t.login.forgot}</Link>}
        />
        {captcha.widget}
        <Button type="submit" variant="primary" fullWidth loading={pending}>
          {t.login.submit}
        </Button>
      </form>
    </AuthLayout>
  )
}
